import { forwardRef, JSX, KeyboardEvent, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import * as L from 'leaflet';
import 'leaflet.locatecontrol';
import { AttributionControl, FeatureGroup, GeoJSON, LayerGroup, LayersControl, MapContainer, Polygon, TileLayer, ZoomControl, useMapEvents } from 'react-leaflet';
import { TranslationService } from '../../../services/translation.service';
import './style.scss';
import CustomLocateControl from './components/LocateControl';
import type IPolygon from './models/polygon.model';
import type IBounds from './models/bounds.model';
import FilterControl from './components/FilterControl';
import type { IGeoJson } from './models/geojson.model';

export interface IMap {
  setView(latLng: L.LatLngExpression, zoom: number): void;
  fitBounds(toBounds: IBounds | undefined): void;
  invalidateSize(): void;
}

interface IMapProps {
  mapId?: string;
  zoomControl?: boolean;
  locateControl?: boolean;
  drawLocationControlMarker?: boolean;
  layerControl?: boolean;
  filterControl?: boolean;
  filterClassName?: string;
  ariaLabel: string;
  className?: string;
  markers?: JSX.Element[];
  clusteredMarkers?: JSX.Element[];
  extraMarkers?: JSX.Element[];
  extraControls?: JSX.Element[] | undefined;
  layers?: JSX.Element[];
  bounds?: IBounds;
  maxBounds?: L.LatLngBoundsExpression;
  polygons?: IPolygon[];
  geoJsons?: IGeoJson[];
  center?: L.LatLngExpression;
  zoomLevel?: number;
  maxBoundsViscosity?: number;
  onZoomEnd?: (currentZoomLevel: number) => void;
  onBoundsChange?: (bounds: L.LatLngBounds) => void;
  onFilterControlClick?: () => void;
  onLocateControlClick?: () => void;
  onClick?: () => void;
  onDragStart?: () => void;
}

export default forwardRef<IMap, IMapProps>(function Map(
  {
    mapId = undefined,
    zoomControl = true,
    locateControl = true,
    drawLocationControlMarker = true,
    layerControl = false,
    filterControl = false,
    filterClassName = '',
    ariaLabel,
    className = '',
    markers = undefined,
    clusteredMarkers = undefined,
    extraMarkers = undefined,
    extraControls = undefined,
    layers = undefined,
    bounds = undefined,
    maxBounds = undefined,
    polygons = undefined,
    geoJsons = undefined,
    center = undefined,
    zoomLevel = 14,
    maxBoundsViscosity = 0.0,
    onZoomEnd = undefined,
    onBoundsChange = undefined,
    onFilterControlClick = undefined,
    onLocateControlClick = undefined,
    onClick = undefined,
    onDragStart = undefined,
  },
  ref,
) {
  const mapRef = useRef<L.Map | null>(null);
  const keyboardOverlayRef = useRef<HTMLDivElement | null>(null);
  const initialBoundsReportedRef = useRef(false);
  const urlTemplateDefault = 'https://osm.vasttrafik.se/styles/osm_vt_basic/{z}/{x}/{y}.png';
  const attribution = '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  const attributionPrefix = '<a href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a>';

  // Inspired by https://github.com/Leaflet/Leaflet/blob/f9bca64e87d319f974585100ba7fb54d301805fe/src/map/handler/KeyboardHandler.js#L24
  const handleKeyboardOverlayKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const map = mapRef.current;

    if (!map) return;

    const panOffset = 80;

    switch (event.key) {
      case 'ArrowUp':
        map.panBy([0, -panOffset]);
        event.preventDefault();
        break;
      case 'ArrowDown':
        map.panBy([0, panOffset]);
        event.preventDefault();
        break;
      case 'ArrowLeft':
        map.panBy([-panOffset, 0]);
        event.preventDefault();
        break;
      case 'ArrowRight':
        map.panBy([panOffset, 0]);
        event.preventDefault();
        break;
      case '+':
      case 'Add':
        map.zoomIn();
        event.preventDefault();
        break;
      case '-':
      case 'Subtract':
        map.zoomOut();
        event.preventDefault();
        break;
      default:
        break;
    }
  }, []);

  function MapSetup() {
    const map = useMapEvents({
      zoomend: () => {
        onZoomEnd?.(map.getZoom());
        map.invalidateSize();
      },
      moveend: () => {
        onBoundsChange?.(map.getBounds());
      },
      click: (event) => {
        const target = event.originalEvent.target;
        // Ignore clicks on Leaflet controls to prevent unintended interactions due to event bubbling when using the keyboard overlay
        if (target instanceof Element && target.closest('.leaflet-control')) return;
        onClick?.();
      },
      dragstart: () => {
        onDragStart?.();
      },
    });

    mapRef.current = map;

    // Move the keyboard overlay div to be a direct child of the map container to ensure it captures keyboard events correctly
    useEffect(() => {
      map.whenReady(() => {
        const overlay = keyboardOverlayRef.current;
        if (!overlay) return;

        const container = map.getContainer();
        if (container.firstElementChild !== overlay) container.prepend(overlay);

        if (!initialBoundsReportedRef.current) {
          initialBoundsReportedRef.current = true;
          onBoundsChange?.(map.getBounds());
        }
      });
    }, [map]);

    useImperativeHandle(ref, () => ({
      setView(latLng: L.LatLngExpression, zoom: number) {
        map.setView(latLng, zoom);
      },
      fitBounds(toBounds: IBounds | undefined) {
        if (toBounds?.padding !== undefined && toBounds.bounds && toBounds.bounds.isValid()) {
          map.fitBounds(toBounds.bounds.pad(toBounds.padding));
        }
      },
      invalidateSize() {
        map.invalidateSize();
      },
    }));

    if (bounds && bounds.bounds.isValid()) {
      if (bounds.method === 'panTo') {
        map.setZoom(zoomLevel);
        map.panTo(bounds.bounds.getCenter());
      } else if (bounds.method === 'fitBounds') {
        map.fitBounds(bounds.bounds.pad(bounds.padding ?? 0));
      } else if (bounds.method === 'flyTo') {
        map.flyTo(bounds.bounds.getCenter(), zoomLevel);
      }
    }

    if (maxBounds) {
      map.fitBounds(maxBounds);
      map.setMaxBounds(maxBounds);
    }

    return null;
  }

  return (
    <MapContainer
      id={mapId}
      center={center || [57.69676, 11.99392]}
      zoom={zoomLevel}
      keyboard={false}
      scrollWheelZoom={true}
      className={`vt-map text-center p-3 p-md-5 ${className}`}
      zoomControl={false}
      attributionControl={false}
      preferCanvas={false}
      maxBoundsViscosity={maxBoundsViscosity}
    >
      <div ref={keyboardOverlayRef} className="vt-map__keyboard-overlay" tabIndex={0} role="button" aria-label={ariaLabel} onKeyDown={handleKeyboardOverlayKeyDown}></div>
      <MapSetup />
      <TileLayer attribution={attribution} url={urlTemplateDefault} />
      <AttributionControl position="bottomright" prefix={attributionPrefix} />
      {extraMarkers && <FeatureGroup>{extraMarkers}</FeatureGroup>}
      {markers && <FeatureGroup>{markers}</FeatureGroup>}
      {clusteredMarkers?.map((x) => x)}
      {/* NV-8585: Use a key to force re-rendering of the FeatureGroup when drawLocationControlMarker changes, to ensure the LocateControl stays in the same place */}
      <FeatureGroup key={drawLocationControlMarker ? 'with-marker' : 'without-marker'}>
        {filterControl === true && <FilterControl onClick={onFilterControlClick} className={filterClassName} />}
        {extraControls?.map((x) => x)}
        {locateControl && <CustomLocateControl position="bottomright" drawMarker={drawLocationControlMarker} onClick={onLocateControlClick} />}
        {zoomControl && <ZoomControl position="bottomright" zoomInTitle={TranslationService.get('common.map.zoomin')} zoomOutTitle={TranslationService.get('common.map.zoomout')} />}
        {layerControl && <LayersControl position="topright"></LayersControl>}
      </FeatureGroup>
      {polygons?.map((x) => (
        <Polygon key={x.id} positions={x.latLngs} pathOptions={x.options} />
      ))}
      {layers && <LayerGroup>{layers}</LayerGroup>}
      {geoJsons?.map((x) => (
        <GeoJSON key={x.id} data={x.data} pathOptions={x.options} interactive={false} />
      ))}
    </MapContainer>
  );
});
